/**
 * TalkToast Color System
 * Brand Colors v0.1 - Platform-agnostic color tokens
 *
 * These colors work across web (CSS/Tailwind) and mobile (React Native StyleSheet)
 */

export const colors = {
  // Brand Colors - Light Mode
  warmCream: '#FFF6ED',        // Background (Base) - airy, soft warmth
  butterCream: '#FDEDD9',      // Surface (Cards) - panels, input areas
  deepCharcoal: '#33261E',     // Text (Primary) - high legibility
  toastGold: '#F4A340',        // Accent - Toast Gold (core brand)
  avocadoGreen: '#64B05A',     // Accent - Fresh Green
  coralPop: '#FF7C6A',         // Accent - Coral (vibrant highlight)
  softTan: '#E8D2B8',          // Outline/Divider - gentle contrast
  honeyYellow: '#FFD77E',      // Accent - Honey Yellow

  // Brand Colors - Dark Mode (for future)
  warmCharcoal: '#3A2F28',     // Dark background
  cocoaBrown: '#4B3C33',       // Dark surface
  creamWhiteDark: '#FFF3E4',   // Dark mode text
  cocoaLine: '#5C4A3E',        // Dark mode outline

  // Semantic Colors
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',

  // Feedback Colors
  success: '#64B05A',          // Same as avocadoGreen
  error: '#FF7C6A',            // Same as coralPop
  warning: '#FFD77E',          // Same as honeyYellow
  info: '#F4A340',             // Same as toastGold

  // Gray Scale (generated from deepCharcoal)
  gray: {
    50: '#F9F7F6',
    100: '#F3F0ED',
    200: '#E6E1DC',
    300: '#D9D2CA',
    400: '#A39889',
    500: '#6D5E48',
    600: '#574B3A',
    700: '#413828',
    800: '#33261E',             // Same as deepCharcoal
    900: '#1A130F',
  }
} as const

export type ColorKey = keyof typeof colors
export type Color = typeof colors[ColorKey]

/**
 * CSS Variable names (for web)
 * These map to the colors above
 */
export const cssVars = {
  // Brand
  '--color-warm-cream': colors.warmCream,
  '--color-butter-cream': colors.butterCream,
  '--color-deep-charcoal': colors.deepCharcoal,
  '--color-toast-gold': colors.toastGold,
  '--color-avocado-green': colors.avocadoGreen,
  '--color-coral-pop': colors.coralPop,
  '--color-soft-tan': colors.softTan,
  '--color-honey-yellow': colors.honeyYellow,

  // Dark mode
  '--color-warm-charcoal': colors.warmCharcoal,
  '--color-cocoa-brown': colors.cocoaBrown,
  '--color-cream-white-dark': colors.creamWhiteDark,
  '--color-cocoa-line': colors.cocoaLine,

  // Semantic
  '--color-success': colors.success,
  '--color-error': colors.error,
  '--color-warning': colors.warning,
  '--color-info': colors.info,
} as const
